<!--
NoSubscriptionBanner — presentational banner for franchises with NO live
access (Fase A, decisão do usuário 2026-08-12).

Receives the structured 'subscription_inactive' keyed banner payload from
useGlobalBanners:
  { key: 'subscription_inactive', items: [{ franchise_name, franchise_id, status, days_left }], severity: 'error' }

Irmão vermelho do GraceBanner (mesma linguagem visual pós-redesign): faixa
sólida, ícone, título, chips por franquia. Sem recálculo de nada — só
apresenta o payload que o composable já decidiu.

Severidade é sempre 'error' — franquia sem acesso é sempre crítico, não há
banda de urgência aqui (contraste com o escalonamento por dias do GraceBanner).

CTA "Reassinar" só aparece para quem pode se reassinar — a pergunta é
respondida por `canSelfSubscribe()` no useSubscriptionStatus, a MESMA função
que o `resolveLockDestination` do lock usa (A13). Full nunca chega a ver este
banner (useSubscriptionStatus limpa para Full), mas a checagem fica aqui por
completude e simetria com a regra do router/App.vue.
-->

<template>
  <div
    class="no-subscription-banner"
    data-severity="error"
    data-testid="no-subscription-banner"
  >
    <i class="no-subscription-banner__icon pi pi-ban" />
    <div class="no-subscription-banner__body">
      <strong class="no-subscription-banner__title">
        {{ t('Franquia sem assinatura ativa — dados suspensos') }}
      </strong>
      <span class="no-subscription-banner__message">
        <span
          v-for="item in items"
          :key="item.franchise_id"
          class="no-subscription-banner__item"
        >
          <strong>{{ item.franchise_name }}</strong>
          {{ ' ' }}· {{ statusLabel(item.status) }}
        </span>
      </span>
    </div>
    <AppButton
      v-if="showCta"
      :label="t('Reassinar')"
      icon="pi pi-arrow-right"
      icon-pos="right"
      size="small"
      class="no-subscription-banner__cta"
      data-testid="no-subscription-banner-cta"
      @click="router.push(BILLING_RECOVERY_PATH)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { permissionsState } from '../services/permissionsService'
import { canSelfSubscribe, BILLING_RECOVERY_PATH } from '../composables/useSubscriptionStatus'
import type { GlobalBanner } from '../composables/useGlobalBanners'

interface InactiveItem {
  franchise_name: string
  franchise_id: number
  status: string | null
  days_left: number | null
}

interface NoSubscriptionBannerPayload extends GlobalBanner {
  items: InactiveItem[]
  severity: 'error'
}

const props = defineProps<{
  banner: NoSubscriptionBannerPayload
}>()

const { t } = useI18n()
const router = useRouter()
// Optional chained: the integration mount installs no router. The CTA
// navigates to /minhas-assinaturas — pointless when already there.
const route = useRoute()

const items = computed(() => props.banner.items)

/**
 * A13 (R3, 2026-08-13): a lista de grupos NÃO mora mais aqui. `canSelfSubscribe`
 * é a mesma função que o `resolveLockDestination` do lock usa, no composable —
 * um dono só. Antes, esta cópia podia divergir do destino do lock em silêncio
 * (usuário destrancado para /minhas-assinaturas, mas sem CTA para chegar lá).
 */
const showCta = computed<boolean>(() =>
  canSelfSubscribe(permissionsState.user?.groups) && route?.path !== BILLING_RECOVERY_PATH,
)

/** Rótulo por status — definição pinada (Fase A, 2026-08-12). */
function statusLabel(status: string | null): string {
  if (status === 'cancelled' || status === 'superseded') return t('cancelada')
  if (status === 'pending') return t('aguardando pagamento')
  return t('sem assinatura')
}
</script>

<style scoped>
/* Mesma linguagem visual do GraceBanner (host GlobalBanners zera o próprio
   padding via :has(.no-subscription-banner)) — versão vermelha permanente,
   sem banda de severidade (sempre crítico). */
.no-subscription-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.85rem 1.5rem;
  background: #dc2626;
  color: #ffffff;
}

.no-subscription-banner__icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.no-subscription-banner__body {
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.no-subscription-banner__title {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.no-subscription-banner__message {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  font-size: 0.9rem;
}

.no-subscription-banner__item {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  white-space: nowrap;
}

.no-subscription-banner__cta {
  flex-shrink: 0;
  font-weight: 700;
}

.no-subscription-banner :deep(.p-button) {
  background: #ffffff;
  border-color: #ffffff;
  color: #b91c1c;
  font-weight: 700;
}
</style>
